import React, { useMemo, useState, useEffect, useRef } from 'react';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import { collectionItemKey } from '../utils/collectionStorage';

export default function MessageCollectionModal({
  sessions,
  isOpen = true,
  existingKeys = [],
  onClose,
  onAddToCollection,
}) {
  const [expanded, setExpanded] = useState(() => new Set(sessions.map((s) => s.id)));
  const [selected, setSelected] = useState(() => new Set());
  const anchorKeyRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set(Array.isArray(existingKeys) ? existingKeys : []));
    anchorKeyRef.current = null;
  }, [isOpen, existingKeys]);

  /** Visible rows only (expanded sessions), top-to-bottom order for shift-range */
  const flatKeys = useMemo(() => {
    const keys = [];
    for (const session of sessions) {
      if (!expanded.has(session.id)) continue;
      for (const r of session.results || []) {
        keys.push(collectionItemKey(session.id, r.email.id));
      }
    }
    return keys;
  }, [sessions, expanded]);

  const toggleSession = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyRange = (fromKey, toKey, checked) => {
    const a = flatKeys.indexOf(fromKey);
    const b = flatKeys.indexOf(toKey);
    if (a < 0 || b < 0) return;
    const start = Math.min(a, b);
    const end = Math.max(a, b);
    setSelected((prev) => {
      const next = new Set(prev);
      for (let i = start; i <= end; i++) {
        const k = flatKeys[i];
        if (checked) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  };

  const handleCheckboxInteraction = (sessionId, emailId, shiftKey) => {
    const key = collectionItemKey(sessionId, emailId);
    const a = anchorKeyRef.current != null ? flatKeys.indexOf(anchorKeyRef.current) : -1;
    const b = flatKeys.indexOf(key);

    if (shiftKey && anchorKeyRef.current != null && a >= 0 && b >= 0) {
      const willCheck = !selected.has(key);
      applyRange(anchorKeyRef.current, key, willCheck);
      return;
    }

    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    anchorKeyRef.current = key;
  };

  const selectAllInSession = (session) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allKeys = (session.results || []).map((r) => collectionItemKey(session.id, r.email.id));
      const allSelected = allKeys.length > 0 && allKeys.every((k) => next.has(k));
      if (allSelected) {
        allKeys.forEach((k) => next.delete(k));
      } else {
        allKeys.forEach((k) => next.add(k));
      }
      return next;
    });
    const last = (session.results || []).slice(-1)[0];
    if (last) anchorKeyRef.current = collectionItemKey(session.id, last.email.id);
  };

  const handleAdd = () => {
    const items = [];
    for (const s of sessions) {
      for (const r of s.results || []) {
        const key = collectionItemKey(s.id, r.email.id);
        if (selected.has(key)) {
          items.push({ sessionId: s.id, sessionTitle: s.title, result: r });
        }
      }
    }
    onAddToCollection(items);
    onClose();
  };

  const totalSelectable = useMemo(
    () => sessions.reduce((n, s) => n + (s.results?.length || 0), 0),
    [sessions]
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Pick messages</h2>
          <button type="button" className="btn btn-sm" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', marginTop: 0 }}>
          Choose messages from any saved analysis run. Items already in <strong>My messages</strong> are pre-checked.
          Hold <strong>Shift</strong> and click another row to select the range in between (within expanded sections).
        </p>
        <div style={{ overflowY: 'auto', flex: 1, marginTop: 8, borderTop: '1px solid var(--surface-3)' }}>
          {sessions.length === 0 && (
            <p style={{ color: 'var(--text-dim)', padding: 16 }}>No saved runs yet. Analyze some mail first.</p>
          )}
          {sessions.map((session) => {
            const open = expanded.has(session.id);
            const results = session.results || [];
            const keys = results.map((r) => collectionItemKey(session.id, r.email.id));
            const allOn = keys.length > 0 && keys.every((k) => selected.has(k));
            return (
              <div key={session.id} style={{ borderBottom: '1px solid var(--surface-3)' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 4px',
                    cursor: 'pointer',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleSession(session.id)}
                    style={{ background: 'none', border: 'none', color: 'var(--text)', cursor: 'pointer' }}
                  >
                    {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }} onClick={() => toggleSession(session.id)}>
                    <div style={{ fontWeight: 600 }}>{session.title}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                      {results.length} message{results.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <button type="button" className="btn btn-sm" onClick={() => selectAllInSession(session)}>
                    {allOn ? 'Clear run' : 'All in run'}
                  </button>
                </div>
                {open &&
                  results.map((r) => {
                    const key = collectionItemKey(session.id, r.email.id);
                    const inCollection = existingKeys.includes(key);
                    return (
                      <label
                        key={r.email.id}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          padding: '8px 8px 8px 36px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(key)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            handleCheckboxInteraction(session.id, r.email.id, e.shiftKey);
                          }}
                          aria-label={`Select ${r.email.subject || 'message'}`}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 500 }}>{r.email.subject || '(No subject)'}</div>
                          <div style={{ color: 'var(--text-dim)' }}>
                            {r.email.sender}
                            {inCollection && (
                              <span style={{ marginLeft: 8, color: 'var(--cyan)', fontSize: '0.75rem' }}>
                                · in My messages
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <span style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
            {selected.size} of {totalSelectable} selected
            {existingKeys.length > 0 && (
              <span style={{ display: 'block', fontSize: '0.8rem', marginTop: 4 }}>
                My messages currently has {existingKeys.length} item{existingKeys.length !== 1 ? 's' : ''}
              </span>
            )}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleAdd} disabled={selected.size === 0}>
              Add to My messages
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
